'use client'

import { useQueries } from '@tanstack/react-query'
import Link from 'next/link'
import { useEffect, useMemo, useState, type ComponentProps, type MouseEvent } from 'react'

import { pushAddToCart } from '@/_utils/analytics/datalayer'
import { fetchYearProgram } from '@/app/[locale]/order/_services/useGetYearProgram'
import {
	LinkProvider,
	MAnnualProgramPreview,
	useProgramSwitcher
} from '@medante/react-components'

import type { IMAnnualProgramPreviewProps } from '@medante/react-components/blocks/program/annual-program-preview'

import {
	getItemCategory2FromConfigLabel,
	pushAnnualProgramSlugViewItem
} from '../_utils/annual-program-slug-ga4.utils'

import type { AnnualProgramSlugGa4Config } from '../_types/annual-program-slug-ga4.types'
import type { IAnnualProgram } from '@/_types'

type LinkProps = ComponentProps<typeof Link>

interface AnnualProgramPreviewAnalyticsProps extends IMAnnualProgramPreviewProps {
	ga4?: AnnualProgramSlugGa4Config
}

function parseOrderHref(href: LinkProps['href']): { type: string; id: number } | null {
	const hrefStr =
		typeof href === 'string'
			? href
			: typeof href === 'object' && href !== null && 'pathname' in href
				? `${href.pathname ?? ''}${'search' in href && href.search ? href.search : ''}`
				: null

	if (!hrefStr || !hrefStr.includes('volne-terminy')) return null

	try {
		const url = new URL(hrefStr, 'https://medante.local')
		const type = url.searchParams.get('type')
		const id = Number(url.searchParams.get('id'))
		if (!type || !id) return null
		return { type, id }
	} catch {
		return null
	}
}

export function AAnnualProgramPreviewAnalytics(props: AnnualProgramPreviewAnalyticsProps) {
	const { ga4, ...previewProps } = props
	const programConfigs = useMemo(
		() => previewProps.annualProgramsConfig ?? [],
		[previewProps.annualProgramsConfig]
	)
	const programIds = useMemo(
		() => programConfigs.map(c => c.programId).filter(id => id > 0),
		[programConfigs]
	)
	const [selectedIndex, setSelectedIndex] = useState(0)

	const programQueries = useQueries({
		queries: programIds.map(id => ({
			queryKey: ['year-program', id],
			queryFn: () => fetchYearProgram(id),
			enabled: id > 0
		}))
	})

	const programs = useMemo(() => {
		return programIds
			.map((id, idx) => programQueries[idx]?.data?.data)
			.filter((program): program is IAnnualProgram => program != null)
	}, [programQueries, programIds])

	const programsById = useMemo(() => {
		const map = new Map<number, IAnnualProgram>()
		programQueries.forEach((query, idx) => {
			const program = query.data?.data
			if (program) map.set(programIds[idx], program)
		})
		return map
	}, [programQueries, programIds])

	const category2ByProgramId = useMemo(() => {
		const map = new Map<number, string>()
		for (const config of programConfigs) {
			const itemCategory2 = getItemCategory2FromConfigLabel(config.name)
			if (itemCategory2) {
				map.set(config.programId, itemCategory2)
			}
		}
		return map
	}, [programConfigs])

	useProgramSwitcher({ programs, onProgramChange: setSelectedIndex })

	useEffect(() => {
		const handleSwitcherClick = (event: Event) => {
			const target = event.target as Element
			const button = target.closest?.('.m-annual-program-preview__switcher button')
			if (!button) return

			const switcher = button.closest('.m-annual-program-preview__switcher')
			if (!switcher) return

			const buttons = switcher.querySelectorAll('button')
			const index = Array.from(buttons).indexOf(button as HTMLButtonElement)
			if (index >= 0) setSelectedIndex(index)
		}

		document.addEventListener('click', handleSwitcherClick)
		return () => document.removeEventListener('click', handleSwitcherClick)
	}, [])

	const effectiveIndex = Math.min(selectedIndex, Math.max(0, programConfigs.length - 1))
	const currentProgramConfig = programConfigs[effectiveIndex] ?? programConfigs[0]

	useEffect(() => {
		if (!currentProgramConfig || !ga4) return

		const program = programsById.get(currentProgramConfig.programId)
		const isLoading = programQueries.some(query => query.isPending)
		if (isLoading && !program) return

		pushAnnualProgramSlugViewItem(
			ga4,
			currentProgramConfig.programId,
			program,
			category2ByProgramId.get(currentProgramConfig.programId)
		)
	}, [
		ga4,
		currentProgramConfig,
		programsById,
		category2ByProgramId,
		programQueries,
		effectiveIndex
	])

	const AnalyticsLink = useMemo(() => {
		function AnnualProgramAnalyticsLink({ href, onClick, ...rest }: LinkProps) {
			const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
				onClick?.(event)
				if (event.defaultPrevented) return

				const parsed = parseOrderHref(href)
				if (!parsed || parsed.type !== 'annual-program') return

				const program = programsById.get(parsed.id)
				if (!program) return

				const item_category2 = category2ByProgramId.get(parsed.id)
				pushAddToCart(
					{
						id: program.id,
						name: program.name,
						type: 'annual-program',
						price: program.price,
						priceOld: program.priceOld
					},
					{ ...(item_category2 !== undefined && { item_category2 }) }
				)
			}

			return <Link href={href} onClick={handleClick} {...rest} />
		}

		return AnnualProgramAnalyticsLink
	}, [programsById, category2ByProgramId])

	return (
		<LinkProvider value={AnalyticsLink as any}>
			<MAnnualProgramPreview {...previewProps} />
		</LinkProvider>
	)
}
